import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { StatusBadge } from "../components/StatusBadge";
import { Plus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatIDR, formatApiError } from "../lib/format";
import { DIVISIONS, PAYABLE_STATUS_LABEL } from "../lib/constants";

export default function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [payables, setPayables] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [vopen, setVopen] = useState(false);
  const [popen, setPopen] = useState(false);
  const [vform, setVform] = useState({ name: "", category: "", contact_person: "", whatsapp: "", address: "", bank_info: "", notes: "" });
  const [pform, setPform] = useState({ vendor_id: "", booking_id: "", division_id: "", description: "", vendor_ref: "", amount: 0, due_date: "", notes: "", status: "Pending" });
  const [payDlg, setPayDlg] = useState(null);
  const [payForm, setPayForm] = useState({ payment_date: new Date().toISOString().slice(0,10), method: "Transfer", notes: "" });

  const load = () => Promise.all([
    api.get("/vendors").then(({ data }) => setVendors(data)),
    api.get("/vendor-payables").then(({ data }) => setPayables(data)),
    api.get("/bookings").then(({ data }) => setBookings(data)),
  ]);
  useEffect(() => { load(); }, []);

  const submitVendor = async () => {
    try { await api.post("/vendors", vform); toast.success("Vendor ditambahkan");
      setVopen(false); setVform({ name: "", category: "", contact_person: "", whatsapp: "", address: "", bank_info: "", notes: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const submitPayable = async () => {
    try { await api.post("/vendor-payables", { ...pform, amount: Number(pform.amount),
        booking_id: pform.booking_id || null, division_id: pform.division_id || null });
      toast.success("Payable dicatat");
      setPopen(false); setPform({ vendor_id: "", booking_id: "", division_id: "", description: "", vendor_ref: "", amount: 0, due_date: "", notes: "", status: "Pending" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const payPayable = async () => {
    try { await api.post("/vendor-payables/pay", { payable_id: payDlg.id, ...payForm });
      toast.success("Payable dibayar"); setPayDlg(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="vendors-page">
      <div>
        <h1 className="font-display text-2xl font-bold">Vendor & Payable</h1>
        <p className="text-sm text-[#5C6066] mt-1">Kelola vendor dan tagihan yang harus dibayar oleh DWG.</p>
      </div>

      <Tabs defaultValue="payables">
        <TabsList className="bg-white border border-[#E5E0D8]">
          <TabsTrigger value="payables" data-testid="tab-payables">Tagihan Vendor</TabsTrigger>
          <TabsTrigger value="vendors" data-testid="tab-vendors">Master Vendor</TabsTrigger>
        </TabsList>

        <TabsContent value="payables" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Dialog open={popen} onOpenChange={setPopen}>
              <DialogTrigger asChild>
                <Button className="bg-brand text-white" data-testid="btn-add-payable"><Plus className="w-4 h-4 mr-1.5" /> Payable Baru</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Tagihan Vendor Baru</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                  <div><Label>Vendor</Label>
                    <Select value={pform.vendor_id} onValueChange={(v) => setPform({ ...pform, vendor_id: v })}>
                      <SelectTrigger className="mt-1.5"><SelectValue placeholder="Pilih vendor" /></SelectTrigger>
                      <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Pemesanan (opsional)</Label>
                    <Select value={pform.booking_id} onValueChange={(v) => setPform({ ...pform, booking_id: v })}>
                      <SelectTrigger className="mt-1.5"><SelectValue placeholder="Pilih pemesanan" /></SelectTrigger>
                      <SelectContent>{bookings.map((b) => <SelectItem key={b.id} value={b.id}>{b.booking_code}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Divisi (opsional)</Label>
                    <Select value={pform.division_id} onValueChange={(v) => setPform({ ...pform, division_id: v })}>
                      <SelectTrigger className="mt-1.5"><SelectValue placeholder="Pilih divisi" /></SelectTrigger>
                      <SelectContent>{DIVISIONS.map((d) => <SelectItem key={d.id} value={d.id}>{d.short}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Deskripsi</Label><Input className="mt-1.5" value={pform.description} onChange={(e) => setPform({ ...pform, description: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Nominal (Rp)</Label><Input type="number" className="mt-1.5" value={pform.amount} onChange={(e) => setPform({ ...pform, amount: e.target.value })} /></div>
                    <div><Label>Jatuh Tempo</Label><Input type="date" className="mt-1.5" value={pform.due_date} onChange={(e) => setPform({ ...pform, due_date: e.target.value })} /></div>
                  </div>
                  <div><Label>Referensi Vendor</Label><Input className="mt-1.5" value={pform.vendor_ref} onChange={(e) => setPform({ ...pform, vendor_ref: e.target.value })} /></div>
                  <div><Label>Catatan</Label><Textarea rows={2} className="mt-1.5" value={pform.notes} onChange={(e) => setPform({ ...pform, notes: e.target.value })} /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setPopen(false)}>Batal</Button>
                  <Button className="bg-brand text-white" onClick={submitPayable} disabled={!pform.vendor_id || !pform.description}>Simpan</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="border border-[#E5E0D8] bg-white shadow-none overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-[#8C9096] bg-[#FAF6EE]">
                    <th className="text-left px-5 py-3">Vendor</th>
                    <th className="text-left px-4 py-3">Deskripsi</th>
                    <th className="text-left px-4 py-3">Pemesanan</th>
                    <th className="text-left px-4 py-3">Jatuh Tempo</th>
                    <th className="text-right px-4 py-3">Nominal</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-right px-4 py-3">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {payables.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[#8C9096]">Belum ada payable.</td></tr>}
                  {payables.map((p) => (
                    <tr key={p.id} className="border-t border-[#F0EBE0] hover:bg-[#F7F4EE]" data-testid={`payable-${p.id}`}>
                      <td className="px-5 py-3">{p.vendor_name}</td>
                      <td className="px-4 py-3">{p.description}</td>
                      <td className="px-4 py-3 font-mono text-xs">{p.booking_code || "-"}</td>
                      <td className="px-4 py-3">{formatDate(p.due_date)}</td>
                      <td className="px-4 py-3 text-right font-mono">{formatIDR(p.amount)}</td>
                      <td className="px-4 py-3"><StatusBadge status={p.status} label={PAYABLE_STATUS_LABEL[p.status]} /></td>
                      <td className="px-4 py-3 text-right">
                        {p.status !== "Paid" && p.status !== "Cancelled" && (
                          <Button variant="outline" size="sm" onClick={() => setPayDlg(p)} data-testid={`btn-pay-${p.id}`}>
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Bayar
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="vendors" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Dialog open={vopen} onOpenChange={setVopen}>
              <DialogTrigger asChild>
                <Button className="bg-brand text-white" data-testid="btn-add-vendor"><Plus className="w-4 h-4 mr-1.5" /> Vendor Baru</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Vendor Baru</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3 py-2">
                  <div className="col-span-2"><Label>Nama</Label><Input className="mt-1.5" value={vform.name} onChange={(e) => setVform({ ...vform, name: e.target.value })} /></div>
                  <div><Label>Kategori</Label><Input className="mt-1.5" value={vform.category} onChange={(e) => setVform({ ...vform, category: e.target.value })} /></div>
                  <div><Label>PIC</Label><Input className="mt-1.5" value={vform.contact_person} onChange={(e) => setVform({ ...vform, contact_person: e.target.value })} /></div>
                  <div><Label>WhatsApp</Label><Input className="mt-1.5" value={vform.whatsapp} onChange={(e) => setVform({ ...vform, whatsapp: e.target.value })} /></div>
                  <div><Label>Alamat</Label><Input className="mt-1.5" value={vform.address} onChange={(e) => setVform({ ...vform, address: e.target.value })} /></div>
                  <div className="col-span-2"><Label>Bank / Pembayaran</Label><Input className="mt-1.5" value={vform.bank_info} onChange={(e) => setVform({ ...vform, bank_info: e.target.value })} /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setVopen(false)}>Batal</Button>
                  <Button className="bg-brand text-white" onClick={submitVendor} disabled={!vform.name}>Simpan</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <Card className="border border-[#E5E0D8] bg-white shadow-none overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-[#8C9096] bg-[#FAF6EE]">
                  <th className="text-left px-5 py-3">Nama</th>
                  <th className="text-left px-4 py-3">Kategori</th>
                  <th className="text-left px-4 py-3">PIC</th>
                  <th className="text-left px-4 py-3">WhatsApp</th>
                </tr>
              </thead>
              <tbody>
                {vendors.map((v) => (
                  <tr key={v.id} className="border-t border-[#F0EBE0]">
                    <td className="px-5 py-3 font-medium">{v.name}</td>
                    <td className="px-4 py-3 text-[#5C6066]">{v.category}</td>
                    <td className="px-4 py-3 text-[#5C6066]">{v.contact_person}</td>
                    <td className="px-4 py-3 text-[#5C6066]">{v.whatsapp}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!payDlg} onOpenChange={(o) => !o && setPayDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Bayar Vendor Payable</DialogTitle></DialogHeader>
          {payDlg && (
            <div className="space-y-3 py-2">
              <div className="text-sm">
                <div className="font-medium">{payDlg.vendor_name}</div>
                <div className="text-[#8C9096]">{payDlg.description}</div>
                <div className="font-mono mt-2 text-lg font-bold">{formatIDR(payDlg.amount)}</div>
              </div>
              <div><Label>Tanggal Bayar</Label><Input type="date" className="mt-1.5" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} /></div>
              <div><Label>Metode</Label><Input className="mt-1.5" value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} /></div>
              <div><Label>Catatan</Label><Textarea rows={2} className="mt-1.5" value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayDlg(null)}>Batal</Button>
            <Button className="bg-brand text-white" onClick={payPayable} data-testid="btn-confirm-pay">Konfirmasi Bayar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
